<template>
  <div
    v-if="isMonetizationEnabled && !sponsoredItemsDisbaled"
    id="sponsored-items-container"
    ref="sponsoredItemsRef"
  >
    <Skeleton
      v-if="loading"
      height="400px"
    />
    <template v-else>
      <div
        v-if="isSiteMonCallSuccessful"
        class="sponsored-items"
        :class="siteMonConfig.customStyleClass"
      >
        <ProductCarousel
          :products="sponsoredProducts"
          :number-of-items="isMcom ? carouselLayout : bcomCarouselLayout"
          :title="carouselTitle"
          :use-wish-list="isMcom"
          :open-in-new-tab="!isMobile && isOpenInNewTabEnabled"
          :show-corner-badge="true"
          :is-simplification-on="isSimplificationOnEnabled"
          :override-price-simplification="overridePriceSimplification"
          :simplification-with-v1="simplificationWithV1"
          :simplification-non-bold-percentage-exp="simplificationNonBoldPercentageExp"
          :simplification-remove-sale-exp="simplificationRemoveSaleExp"
          :show-review-condensed="showReviewCondensed"
          :sponsored-label="showSponsoredLabel"
          @link-click-event="(productId, position) => {
            analyticsUtil.carouselClickEvent({
              productId,
              position,
              sponsoredProducts,
              device: siteMonConfig.device,
              page: pageType,
              clickTagEnabled: enableClickTagsForSMProducts,
              clickAnalyticsFired,
            })
          }"
          @pixel-presented-event="(productsInView) => {
            analyticsUtil.onLoadEvent({
              selector: '.sponsored-items',
              viewAnalyticsFired,
              productsInView,
              sponsoredProducts,
              device: siteMonConfig.device,
              page: pageType,
              viewTagEnabled: enableImpressionTagsForSMProducts,
              carouselBeacons,
            })
          }"
          @scroll-event="(e) => analyticsUtil.carouselScrollEvent(viewAnalyticsFired, e, sponsoredProducts)"
          @on-left-chevron-click="onArrowClicked('left')"
          @on-right-chevron-click="onArrowClicked('right')"
        >
          <template #title>
            <div class="h3">
              {{ carouselTitle }}
            </div>
          </template>
        </ProductCarousel>
      </div>
    </template>
  </div>
</template>

<script>
import {
  ref, onMounted, watch, computed,
} from 'vue';
import { useStore } from 'vuex';
import { ProductCarousel } from '@common-vue/product-carousel';
import Skeleton from '@atomic-ui/skeleton';
import analyticsUtil from '../utils/analyticsUtil';
import {
  isSimplificationOnExperimentEnabled, isSimplificationWithV1ExpEnabled,
  isSimplificationNonBoldPercentageExpEnabled, isSimplificationRemoveSaleExpEnabled, isShowReviewCondensed,
  isRedesignCoreCarouselEnabled, isConsolidatedProductDescription,
} from '../utils/experimentUtil';
import { lazyLoadXAPICall, useLazyLoading, usePDPLazyLoading } from '../utils/visabilityHelper';
import useSponsoredData from '../composables/useSponsoredData';

export default {
  name: 'SponsoredItems',
  components: {
    ProductCarousel,
    Skeleton,
  },
  props: {
    siteMonConfig: {
      type: Object,
      required: true,
    },
    isMonetizationEnabled: {
      type: Boolean,
      required: true,
      defaut: false,
    },
    enableImpressionTagsForSMProducts: {
      type: Boolean,
      required: false,
      default: false,
    },
    enableClickTagsForSMProducts: {
      type: Boolean,
      required: false,
      default: false,
    },
    carouselOpenInNewTabEnabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    isUSARegion: {
      type: Boolean,
      required: false,
      default: true,
    },
    isWeddingRegistry: {
      type: Boolean,
      required: false,
      default: false,
    },
    isMcom: {
      type: Boolean,
      required: false,
      default: true,
    },
    selectedUpc: {
      type: String,
      required: false,
      default: '',
    },
  },
  emits: [
    'sponsoredItemsLoaded',
  ],
  setup(props, context) {
    const showSponsoredLabel = ref(false);
    const sponsoredItemsRef = ref(null);
    const isRightArrowClicked = ref(false);
    const isLeftArrowClicked = ref(false);
    const viewAnalyticsFired = [];
    const clickAnalyticsFired = [];
    const loading = ref(true);
    const bcomCarouselLayout = {
      small: 2.5,
      medium: 5.5,
      large: 6,
    };
    const sponsoredProducts = ref([]);
    const isSiteMonCallSuccessful = ref(false);
    const carouselTitle = ref('');
    const isMobile = props.siteMonConfig.device === 'mobile';
    const carouselBeacons = ref({});
    const redesignCoreCarouselEnabled = isRedesignCoreCarouselEnabled();
    const carouselLayout = {
      small: 2.45,
      medium: 5.3,
      large: redesignCoreCarouselEnabled ? 6 : 5.2,
    };
    const isSimplificationOnEnabled = props.siteMonConfig?.isSimplificationOn && isSimplificationOnExperimentEnabled();
    const simplificationWithV1 = isSimplificationWithV1ExpEnabled();
    const simplificationNonBoldPercentageExp = isSimplificationNonBoldPercentageExpEnabled();
    const simplificationRemoveSaleExp = isSimplificationRemoveSaleExpEnabled();
    const overridePriceSimplification = isSimplificationOnEnabled;
    const isOpenInNewTabEnabled = props.carouselOpenInNewTabEnabled || false;
    const sponsoredItemsDisbaled = props.isWeddingRegistry || !props.isUSARegion;
    const showReviewCondensed = isShowReviewCondensed();
    const lineClamp = isConsolidatedProductDescription() ? 1 : 2;
    let pageType = props.siteMonConfig.sponsoredItemsConfig.pageType || props.siteMonConfig.clientId;
    pageType = pageType === 'product' ? 'pdp' : pageType;
    onMounted(() => {
      if (props.isMonetizationEnabled) {
        const store = useStore();
        const useSponsoredDataParams = {
          clientId: props.siteMonConfig.clientId,
          parameters: props.siteMonConfig.sponsoredItemsConfig,
          device: props.siteMonConfig.device,
          loading,
          monetizationComponent: 'sponsored items',
          isSiteMonCallSuccessful,
          carouselTitle,
          sponsoredProducts,
          carouselBeacons,
          store,
          isMcom: props.isMcom,
          isSimplificationOnEnabled,
          selectedUpcId: computed(() => props.selectedUpc),
          showSponsoredLabel,
        };
        if (props.siteMonConfig.clientId === 'product') {
          if (props.siteMonConfig.sponsoredItemsConfig.showSponsoredItemTop) {
            const sponsoredItemTopComp = computed(() => store.getters['sponsoredItemTopStore/getSponsoredItemTopRef']);
            if (props.siteMonConfig?.collapsedProductDetails) {
              usePDPLazyLoading(sponsoredItemTopComp, sponsoredItemsRef, useSponsoredDataParams);
            } else {
              watch(sponsoredItemTopComp, () => {
                if (sponsoredItemTopComp.value) {
                  useSponsoredData(useSponsoredDataParams);
                }
              });
            }
          } else {
            useLazyLoading(sponsoredItemsRef, useSponsoredDataParams);
          }
        } else {
          const isPagination = computed(() => store.getters['sponsoredItemsStore/getIsPagination']);
          if (!isPagination.value) {
            store.dispatch('sponsoredItemsStore/setIsPagination');
            lazyLoadXAPICall('#sponsored-items-container', useSponsoredDataParams);
          } else {
            setTimeout(() => {
              lazyLoadXAPICall('#sponsored-items-container', useSponsoredDataParams);
            }, 1000);
          }
        }
      }
    });
    watch(isSiteMonCallSuccessful, (newValue) => {
      if (newValue) context.emit('sponsoredItemsLoaded');
    });
    const onArrowClicked = (arrowDir) => {
      analyticsUtil.handleArrowClick(arrowDir, isRightArrowClicked, isLeftArrowClicked, pageType);
    };
    return {
      sponsoredProducts,
      isSiteMonCallSuccessful,
      loading,
      carouselLayout,
      bcomCarouselLayout,
      carouselTitle,
      analyticsUtil,
      viewAnalyticsFired,
      clickAnalyticsFired,
      onMounted,
      onArrowClicked,
      isMobile,
      carouselBeacons,
      pageType,
      sponsoredItemsRef,
      isSimplificationOnEnabled,
      sponsoredItemsDisbaled,
      overridePriceSimplification,
      simplificationWithV1,
      simplificationNonBoldPercentageExp,
      simplificationRemoveSaleExp,
      isOpenInNewTabEnabled,
      showReviewCondensed,
      lineClamp,
      showSponsoredLabel,
    };
  },
};
</script>

<style lang="scss" scoped>
@import "~settingSCSS";
</style>

<style lang="scss" mcom>
@include breakpoint(large up) {
.sponsored-items .product-description .product-name {
  -webkit-line-clamp: 1;
}
}

@include breakpoint(medium down) {
  .sponsored-items .product-description .product-name {
   -webkit-line-clamp: v-bind(lineClamp);
}
}
  .sponsored-items .carousel-container .title-container:has(.header-sponsored-label) {
    padding-right: rem-calc(0);
  }
 .sponsored-items  .toast {
  position: absolute;
  }
</style>
